import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  AlertTriangle,
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  Folder,
  FolderPlus,
  GripVertical,
  Inbox,
  LayoutGrid,
  Link2,
  List as ListIcon,
  Loader2,
  Radio,
  Search as SearchIcon,
  Square,
  Trash2,
  Upload,
  Video,
} from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import {
  importSourceFor,
  type RecordingSummary,
} from "@rec/shared";
import { useServerText } from "@/i18n";
import { useFormat } from "@/i18n/format";
import { api } from "@/lib/api";
import { FolderTree } from "@/components/FolderTree";
import { MediaCard } from "@/components/MediaCard";
import { StatusBadge, statusExplanation } from "@/components/RecordingStatus";
import { UploadButton } from "@/components/UploadButton";
import { ImportLinkButton } from "@/components/ImportLinkDialog";
import { usePlatformLabel } from "@/components/MediaCard";
import { SendBotDialog } from "@/components/SendBotDialog";
import { DeleteImpact } from "@/components/DeleteImpact";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Skeleton,
} from "@/components/ui";
import { cn } from "@/lib/utils";

/**
 * The library: a folder tree beside the recordings in the selected folder.
 *
 * Drag-and-drop moves both — a folder into another folder, or a recording into
 * a folder. Both land on the same `onDragEnd`, distinguished by the dragged
 * item's `data.type`, so there is one place where a move is decided.
 */
type SortKey = "newest" | "oldest" | "longest" | "largest" | "name";

/** Labels are `library:sort.<key>`, translated when rendered. */
const SORT_KEYS: SortKey[] = ["newest", "oldest", "longest", "largest", "name"];

const PAGE_SIZE = 24;

export function Library() {
  const { t } = useTranslation(["library", "common"]);
  const st = useServerText();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [sendingBot, setSendingBot] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [movingRecording, setMovingRecording] = useState<string | null>(null);
  const [folderName, setFolderName] = useState("");
  /** Grid or list — a reading preference, remembered per device. */
  const [view, setView] = useState<"grid" | "list">(() =>
    localStorage.getItem("home.view") === "list" ? "list" : "grid",
  );
  /** Ignore folders and show every video in one flat collection. */
  const [flat, setFlat] = useState(false);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortKey>("newest");
  const [page, setPage] = useState(1);
  const [dragging, setDragging] = useState<{
    type: "folder" | "recording";
    id: string;
    label: string;
  } | null>(null);

  // A new filter means a new first page — a stale page 4 of a filtered list
  // reads as "everything vanished".
  useEffect(() => setPage(1), [selectedFolder, search, sort, flat]);

  const folders = useQuery({ queryKey: ["projects"], queryFn: api.listProjects });

  const recordings = useQuery({
    queryKey: ["recordings"],
    queryFn: api.listRecordings,
    refetchInterval: (query) =>
      // Anything not finished can still change: a bot moves through joining,
      // waiting room, recording and processing without the user doing
      // anything, and each of those is worth seeing as it happens.
      query.state.data?.some(
        (r) => r.status !== "ready" && r.status !== "failed",
      )
        ? 10_000
        : false,
  });

  const moveFolder = useMutation({
    mutationFn: (input: { id: string; parentId: string | null }) =>
      api.moveProject(input.id, input.parentId),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["projects"] }),
  });

  const moveRecording = useMutation({
    mutationFn: (input: { id: string; projectId: string | null }) =>
      api.moveRecording(input.id, input.projectId),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["recordings"] });
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });

  const deleteRecording = useMutation({
    mutationFn: (id: string) => api.deleteRecording(id),
    onSuccess: () => {
      setConfirmDelete(null);
      void queryClient.invalidateQueries({ queryKey: ["recordings"] });
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });

  /**
   * End a live recording early.
   *
   * Leaving a call is not the same as ending it — on Google Meet the room stays
   * open after the last person goes — so without this the only way to finish a
   * recording is to be the host and end the meeting for everyone, which most
   * people cannot do and should not have to.
   */
  const stopRecording = useMutation({
    mutationFn: (id: string) => api.stopRecording(id),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["recordings"] }),
  });

  const renameFolder = useMutation({
    mutationFn: (input: { id: string; name: string }) =>
      api.renameProject(input.id, input.name),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["projects"] }),
  });

  const deleteFolder = useMutation({
    mutationFn: (id: string) => api.deleteProject(id),
    onSuccess: (_result, id) => {
      // Deselect it, or the library keeps filtering by a folder that is gone.
      if (selectedFolder === id) setSelectedFolder(null);
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
      void queryClient.invalidateQueries({ queryKey: ["recordings"] });
    },
  });

  const createFolder = useMutation({
    mutationFn: (name: string) =>
      api.createProject({ name, parentId: selectedFolder }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["projects"] }),
  });

  // A pointer sensor with a small activation distance, so a click on a
  // recording still navigates rather than starting a drag.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  /**
   * The desktop metaphor: what you see is the current folder's contents —
   * its subfolders and its videos — unless `flat` flattens everything or a
   * search sweeps across the whole workspace.
   */
  const childFolders = useMemo(() => {
    if (flat || search.trim()) return [];
    return (folders.data ?? []).filter((f) => f.parentId === selectedFolder);
  }, [folders.data, selectedFolder, flat, search]);

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    const all = recordings.data ?? [];
    const scoped =
      flat || query
        ? all
        : all.filter((r) => r.projectId === selectedFolder);
    const searched = query
      ? scoped.filter((r) => r.title.toLowerCase().includes(query))
      : scoped;

    const by: Record<SortKey, (a: RecordingSummary, b: RecordingSummary) => number> = {
      newest: (a, b) => +new Date(b.startedAt) - +new Date(a.startedAt),
      oldest: (a, b) => +new Date(a.startedAt) - +new Date(b.startedAt),
      longest: (a, b) => (b.durationMs ?? 0) - (a.durationMs ?? 0),
      largest: (a, b) => (b.sizeBytes ?? 0) - (a.sizeBytes ?? 0),
      name: (a, b) => a.title.localeCompare(b.title),
    };
    return [...searched].sort(by[sort]);
  }, [recordings.data, selectedFolder, search, sort, flat]);

  const pageCount = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const clampedPage = Math.min(page, pageCount);
  const pageItems = visible.slice((clampedPage - 1) * PAGE_SIZE, clampedPage * PAGE_SIZE);

  /** The path from the root to the open folder, clickable segment by segment. */
  const breadcrumb = useMemo(() => {
    const chain: Array<{ id: string; name: string }> = [];
    let cursor = selectedFolder;
    const byId = new Map((folders.data ?? []).map((f) => [f.id, f]));
    while (cursor) {
      const folder = byId.get(cursor);
      if (!folder) break;
      chain.unshift({ id: folder.id, name: folder.name });
      cursor = folder.parentId;
    }
    return chain;
  }, [folders.data, selectedFolder]);

  function onDragStart(event: DragStartEvent) {
    const data = event.active.data.current as
      | { type: "folder" | "recording"; id: string; label?: string }
      | undefined;
    if (!data) return;
    setDragging({ type: data.type, id: data.id, label: data.label ?? "" });
  }

  function onDragEnd(event: DragEndEvent) {
    setDragging(null);
    const { active, over } = event;
    if (!over) return;

    const source = active.data.current as
      | { type: "folder" | "recording"; id: string }
      | undefined;
    if (!source) return;

    // Drop targets: `root`, the sidebar\'s `folder:<id>`, or the content
    // surface's `content-folder:<id>` — distinct ids because dnd-kit keeps
    // one node per id, normalised here where every move is decided.
    const overId = String(over.id);
    const targetId =
      overId === "root" ? null : overId.replace(/^(content-)?folder:/, "");

    if (source.type === "folder") {
      if (targetId === source.id) return;
      moveFolder.mutate({ id: source.id, parentId: targetId });
    } else {
      moveRecording.mutate({ id: source.id, projectId: targetId });
    }
  }

  return (
    <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <div className="grid gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
        <aside className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-[13px] font-semibold text-text">{t("folders.heading")}</h2>
            <Button
              variant="ghost"
              size="icon"
              title={t("folders.new")}
              aria-label={t("folders.new")}
              onClick={() => {
                // Inline rather than window.prompt — the only native browser
                // dialog left in an otherwise custom UI, and it cannot be
                // styled, themed, or dismissed the way the rest of the app is.
                setCreatingFolder(true);
              }}
            >
              <FolderPlus size={15} />
            </Button>
          </div>

          {creatingFolder ? (
            <form
              className="mb-2 flex items-center gap-1"
              onSubmit={(e) => {
                e.preventDefault();
                const name = folderName.trim();
                if (name) createFolder.mutate(name);
                setCreatingFolder(false);
                setFolderName("");
              }}
            >
              <input
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                onBlur={() => setCreatingFolder(false)}
                placeholder={t("folders.namePlaceholder")}
                autoFocus
                className="h-8 min-w-0 flex-1 rounded-lg border border-accent bg-bg px-2.5 text-[13px] text-text placeholder:text-text-faint focus:outline-none"
              />
            </form>
          ) : null}

          {folders.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : (
            <FolderTree
              folders={folders.data ?? []}
              selectedId={selectedFolder}
              onSelect={setSelectedFolder}
              draggingId={dragging?.type === "folder" ? dragging.id : null}
              onRename={(id, name) => renameFolder.mutate({ id, name })}
              onDelete={(id) => deleteFolder.mutate(id)}
            />
          )}

          {moveFolder.error ? (
            <p role="alert" className="rounded-lg bg-danger-subtle px-2.5 py-1.5 text-[12px] text-danger">
              {moveFolder.error instanceof Error
                ? st(moveFolder.error.message)
                : t("folders.moveRejected")}
            </p>
          ) : null}
        </aside>

        <div className="min-w-0 space-y-4">
          {/* The browser toolbar: find, arrange, add. */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-0 flex-1 basis-52">
              <SearchIcon
                size={14}
                className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-text-faint"
              />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={flat ? t("toolbar.searchAll") : t("toolbar.search")}
                className="w-full rounded-lg border border-border bg-bg-subtle py-2 pr-3 pl-8 text-[13.5px] text-text placeholder:text-text-faint focus:border-accent focus:outline-none"
              />
            </div>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as SortKey)}
              className="rounded-lg border border-border bg-bg-subtle px-2.5 py-2 text-[13px] text-text focus:border-accent focus:outline-none"
              title={t("sort.label")}
            >
              {SORT_KEYS.map((key) => (
                <option key={key} value={key}>
                  {t(`sort.${key}`)}
                </option>
              ))}
            </select>
            <div className="flex overflow-hidden rounded-lg border border-border">
              {(
                [
                  ["grid", <LayoutGrid key="g" size={14} />, t("toolbar.gridView")],
                  ["list", <ListIcon key="l" size={14} />, t("toolbar.listView")],
                ] as const
              ).map(([key, icon, label]) => (
                <button
                  key={key}
                  type="button"
                  aria-pressed={view === key}
                  title={label}
                  onClick={() => {
                    setView(key);
                    localStorage.setItem("home.view", key);
                  }}
                  className={cn(
                    "px-2.5 py-2 transition-colors",
                    view === key
                      ? "bg-surface-hover text-text"
                      : "bg-bg-subtle text-text-faint hover:text-text",
                  )}
                >
                  {icon}
                </button>
              ))}
            </div>
            <button
              type="button"
              aria-pressed={flat}
              onClick={() => setFlat((f) => !f)}
              title={t("toolbar.flatHint")}
              className={cn(
                "rounded-lg border px-2.5 py-2 text-[13px] transition-colors",
                flat
                  ? "border-accent bg-accent-subtle/30 text-text"
                  : "border-border bg-bg-subtle text-text-muted hover:text-text",
              )}
            >
              {t("toolbar.allVideos")}
            </button>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <UploadButton projectId={selectedFolder} compact />
              <ImportLinkButton projectId={selectedFolder} compact />
              <Button variant="primary" size="sm" onClick={() => setSendingBot(true)}>
                <Video size={14} />
                {t("toolbar.recordMeeting")}
              </Button>
            </div>
          </div>

          {/* Where am I — clickable path, like any file browser. */}
          {!flat && !search.trim() ? (
            <nav
              aria-label={t("folders.path")}
              className="flex flex-wrap items-center gap-1 text-[13px] text-text-muted"
            >
              <button
                type="button"
                onClick={() => setSelectedFolder(null)}
                className={cn(
                  "rounded px-1.5 py-0.5 hover:bg-surface-hover hover:text-text",
                  selectedFolder === null && "font-medium text-text",
                )}
              >
                {t("folders.home")}
              </button>
              {breadcrumb.map((segment, i) => (
                <span key={segment.id} className="flex items-center gap-1">
                  <ChevronRight size={12} className="text-text-faint" />
                  <button
                    type="button"
                    onClick={() => setSelectedFolder(segment.id)}
                    className={cn(
                      "rounded px-1.5 py-0.5 hover:bg-surface-hover hover:text-text",
                      i === breadcrumb.length - 1 && "font-medium text-text",
                    )}
                  >
                    {segment.name}
                  </button>
                </span>
              ))}
              <span className="ml-2 text-[12px] text-text-faint">
                {t("count.videos", { count: visible.length })}
                {childFolders.length > 0
                  ? ` · ${t("count.folders", { count: childFolders.length })}`
                  : ""}
              </span>
            </nav>
          ) : (
            <p className="flex flex-wrap items-center gap-2 text-[12.5px] text-text-faint">
              <span>
                {search.trim()
                  ? t("count.searching", { count: visible.length })
                  : t("count.workspace", { count: visible.length })}
              </span>
              {search.trim() ? (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="rounded px-1.5 py-0.5 text-text-muted underline hover:text-text"
                >
                  {t("toolbar.clearSearch")}
                </button>
              ) : null}
            </p>
          )}

          {recordings.isError ? (
            /*
              An error must never fall through to the empty state. It used to:
              a 500 or a dropped connection rendered "No recordings yet" next to
              a Record button, so total failure looked exactly like a new
              account.
            */
            <ErrorState
              title={t("empty.loadRecordingsFailed")}
              error={recordings.error}
              onRetry={() => void recordings.refetch()}
            />
          ) : recordings.isLoading ? (
            <div className="space-y-3">
              {[0, 1, 2].map((i) => (
                <Card key={i} className="p-5">
                  <Skeleton className="h-5 w-2/5" />
                  <Skeleton className="mt-3 h-4 w-1/4" />
                </Card>
              ))}
            </div>
          ) : visible.length === 0 && childFolders.length === 0 ? (
            <EmptyState
              icon={<Video size={32} strokeWidth={1.5} />}
              title={
                search.trim()
                  ? t("empty.nothingMatches")
                  : selectedFolder
                    ? t("empty.folderEmpty")
                    : t("empty.noRecordings")
              }
              description={
                search.trim()
                  ? t("empty.tryDifferent")
                  : selectedFolder
                    ? t("empty.folderEmptyHint")
                    : t("empty.noRecordingsHint")
              }
              action={
                search.trim() ? undefined : (
                  <div className="flex items-center gap-2">
                    <Button variant="primary" onClick={() => setSendingBot(true)}>
                      <Video size={15} />
                      {t("toolbar.recordMeeting")}
                    </Button>
                    <UploadButton projectId={selectedFolder} />
                    <ImportLinkButton projectId={selectedFolder} />
                  </div>
                )
              }
            />
          ) : view === "grid" ? (
            <>
              {/* Folders first, like a desktop: enter on click, file on drop. */}
              {childFolders.length > 0 ? (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
                  {childFolders.map((folder) => (
                    <FolderTile
                      key={folder.id}
                      id={folder.id}
                      name={folder.name}
                      count={folder.recordingCount}
                      onOpen={() => setSelectedFolder(folder.id)}
                    />
                  ))}
                </div>
              ) : null}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
                {pageItems.map((recording) => (
                  <GridVideoCard
                    key={recording.id}
                    recording={recording}
                    confirming={confirmDelete === recording.id}
                    deleting={deleteRecording.isPending}
                    onOpen={() =>
                      void navigate({
                        to: "/r/$recordingId",
                        params: { recordingId: recording.id },
                      })
                    }
                    onAskDelete={() => setConfirmDelete(recording.id)}
                    onCancelDelete={() => setConfirmDelete(null)}
                    onConfirmDelete={() => deleteRecording.mutate(recording.id)}
                    onStop={() => stopRecording.mutate(recording.id)}
                    onMoveTo={() => setMovingRecording(recording.id)}
                    stopping={
                      stopRecording.isPending &&
                      stopRecording.variables === recording.id
                    }
                  />
                ))}
              </div>
            </>
          ) : (
            <div className="space-y-2.5">
              {childFolders.map((folder) => (
                <FolderListRow
                  key={folder.id}
                  id={folder.id}
                  name={folder.name}
                  count={folder.recordingCount}
                  onOpen={() => setSelectedFolder(folder.id)}
                />
              ))}
              {pageItems.map((recording) => (
                <RecordingCard
                  key={recording.id}
                  recording={recording}
                  confirming={confirmDelete === recording.id}
                  deleting={deleteRecording.isPending}
                  onAskDelete={() => setConfirmDelete(recording.id)}
                  onCancelDelete={() => setConfirmDelete(null)}
                  onConfirmDelete={() => deleteRecording.mutate(recording.id)}
                  onStop={() => stopRecording.mutate(recording.id)}
                  onMoveTo={() => setMovingRecording(recording.id)}
                  stopping={
                    stopRecording.isPending &&
                    stopRecording.variables === recording.id
                  }
                />
              ))}
            </div>
          )}

          {/* Pagination — client-side over the filtered list. */}
          {pageCount > 1 ? (
            <div className="flex items-center justify-center gap-3 pt-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={clampedPage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft size={14} />
                {t("pagination.previous")}
              </Button>
              <span className="text-[12.5px] tabular-nums text-text-muted">
                {t("pagination.pageOf", { page: clampedPage, total: pageCount })}
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={clampedPage >= pageCount}
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              >
                {t("pagination.next")}
                <ChevronRight size={14} />
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      {movingRecording ? (
        <MoveToFolderDialog
          folders={folders.data ?? []}
          onClose={() => setMovingRecording(null)}
          onPick={(projectId) => {
            moveRecording.mutate({ id: movingRecording, projectId });
            setMovingRecording(null);
          }}
        />
      ) : null}

      {sendingBot ? (
        <SendBotDialog
          projectId={selectedFolder}
          onClose={() => setSendingBot(false)}
        />
      ) : null}

      {/* Follows the cursor so it is obvious what is being moved. */}
      <DragOverlay dropAnimation={null}>
        {dragging ? (
          // Sized by its text, not by the grip it was lifted from — anchored
          // to the grip's box the title wrapped one word per line.
          <div className="w-max max-w-64 truncate rounded-lg border border-accent bg-surface px-3 py-2 text-[13px] font-medium text-text shadow-[var(--shadow-lg)]">
            {dragging.label}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

/**
 * A folder as content, not only as navigation: click to enter, drop to file.
 * Uses the same `folder:<id>` droppable id the sidebar tree uses, so one
 * onDragEnd decides every move.
 */
function FolderTile({
  id,
  name,
  count,
  onOpen,
}: {
  id: string;
  name: string;
  count: number;
  onOpen: () => void;
}) {
  const { t } = useTranslation("library");
  const { setNodeRef, isOver } = useDroppable({ id: `content-folder:${id}` });

  return (
    <button
      ref={setNodeRef}
      type="button"
      onClick={onOpen}
      title={t("folders.openOrDrop", { name })}
      className={cn(
        "flex flex-col items-start gap-2 rounded-xl border p-3.5 text-left transition-colors",
        isOver
          ? "border-accent bg-accent-subtle/30"
          : "border-border bg-surface hover:border-border-strong hover:bg-surface-hover",
      )}
    >
      <Folder
        size={26}
        strokeWidth={1.5}
        className={isOver ? "text-accent" : "text-text-faint"}
      />
      <span className="w-full">
        <span dir="auto" className="block truncate text-[13.5px] font-medium text-text">
          {name}
        </span>
        <span className="block text-[11.5px] text-text-faint">
          {t("count.videos", { count })}
        </span>
      </span>
    </button>
  );
}

/** The list view's folder: one row, same click-to-enter and drop-to-file. */
function FolderListRow({
  id,
  name,
  count,
  onOpen,
}: {
  id: string;
  name: string;
  count: number;
  onOpen: () => void;
}) {
  const { t } = useTranslation("library");
  const { setNodeRef, isOver } = useDroppable({ id: `content-folder:${id}` });

  return (
    <button
      ref={setNodeRef}
      type="button"
      onClick={onOpen}
      title={t("folders.openOrDrop", { name })}
      className={cn(
        "flex w-full items-center gap-3 rounded-xl border px-5 py-3.5 text-left transition-colors",
        isOver
          ? "border-accent bg-accent-subtle/30"
          : "border-border bg-surface hover:border-border-strong hover:bg-surface-hover",
      )}
    >
      <Folder size={18} strokeWidth={1.75} className={isOver ? "text-accent" : "text-text-faint"} />
      <span dir="auto" className="min-w-0 flex-1 truncate text-[14px] font-medium text-text">
        {name}
      </span>
      <span className="shrink-0 text-[12px] text-text-faint">
        {t("count.videos", { count })}
      </span>
    </button>
  );
}

/**
 * A video in grid view: the shared MediaCard, draggable onto folders, with
 * the same stop/delete affordances the list rows carry.
 */
function GridVideoCard({
  recording,
  confirming,
  deleting,
  onOpen,
  onAskDelete,
  onCancelDelete,
  onConfirmDelete,
  onStop,
  stopping,
  onMoveTo,
}: {
  recording: RecordingSummary;
  confirming: boolean;
  deleting: boolean;
  onOpen: () => void;
  onAskDelete: () => void;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
  onStop: () => void;
  stopping: boolean;
  /** The keyboard and touch path to filing this, same as the list grip. */
  onMoveTo: () => void;
}) {
  const { t } = useTranslation(["library", "common"]);
  const st = useServerText();
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `recording:${recording.id}`,
    data: { type: "recording", id: recording.id, label: recording.title },
  });

  if (confirming) {
    return (
      <div className="flex flex-col justify-between rounded-xl border border-danger/40 bg-surface p-3.5">
        <div>
          <p className="text-[12.5px] leading-relaxed text-text">
            <Trans
              t={t}
              i18nKey="card.confirmDeleteShort"
              values={{ title: recording.title }}
              components={{ strong: <span className="font-medium" /> }}
            />
          </p>
          <DeleteImpact recordingId={recording.id} />
        </div>
        <div className="mt-3 flex gap-2">
          <Button variant="danger" size="sm" disabled={deleting} onClick={onConfirmDelete}>
            {deleting ? <Loader2 size={13} className="animate-spin" /> : null}
            {t("common:actions.delete")}
          </Button>
          <Button variant="secondary" size="sm" onClick={onCancelDelete}>
            {t("common:actions.cancel")}
          </Button>
        </div>
      </div>
    );
  }

  const live = recording.status === "scheduled" || recording.status === "recording";
  const explanation = statusExplanation(recording);

  /*
    The drag listeners live on a grip, never spread onto the wrapper: that
    produced role=button-inside-role-button with a dead Enter key and an
    accessible name that read the whole card — the exact defect the list
    rows' comment documents. The grip also opens the Move dialog on press,
    which is the keyboard and touch path dragging can never be.
  */
  return (
    <div
      className={cn("group/card relative", isDragging && "opacity-40")}
      title={explanation ? st(explanation) : undefined}
    >
      <MediaCard
        recording={recording}
        onOpen={onOpen}
        action={
          <span className="flex items-center gap-1">
            <button
              type="button"
              ref={setNodeRef}
              {...listeners}
              {...attributes}
              aria-label={t("card.moveNamed", { title: recording.title })}
              title={t("card.dragHint")}
              onClick={(e) => {
                e.stopPropagation();
                onMoveTo();
              }}
              className={cn(
                "touch-show cursor-grab rounded-md bg-black/55 p-1 text-white/80 opacity-0 transition-opacity",
                "hover:text-white active:cursor-grabbing group-hover/card:opacity-100 focus-visible:opacity-100",
              )}
            >
              <GripVertical size={13} />
            </button>
            {live ? (
              <Button
                variant="secondary"
                size="sm"
                disabled={stopping}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onStop();
                }}
              >
                {stopping ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <Square size={11} fill="currentColor" />
                )}
                {t("card.stop")}
              </Button>
            ) : (
              <button
                type="button"
                aria-label={t("card.deleteNamed", { title: recording.title })}
                title={t("card.deleteRecording")}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onAskDelete();
                }}
                className={cn(
                  "touch-show rounded-md bg-black/55 p-1 text-white/80 opacity-0 transition-opacity",
                  "hover:text-danger group-hover/card:opacity-100 focus-visible:opacity-100",
                )}
              >
                <Trash2 size={13} />
              </button>
            )}
          </span>
        }
      />
    </div>
  );
}

function RecordingCard({
  recording,
  confirming,
  deleting,
  onAskDelete,
  onCancelDelete,
  onConfirmDelete,
  onStop,
  stopping,
  onMoveTo,
}: {
  recording: RecordingSummary;
  confirming: boolean;
  deleting: boolean;
  onAskDelete: () => void;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
  onStop: () => void;
  stopping: boolean;
  /** The keyboard and touch path to filing this, since dragging is neither. */
  onMoveTo: () => void;
}) {
  const { t } = useTranslation(["library", "common"]);
  const st = useServerText();
  const f = useFormat();
  const platformLabel = usePlatformLabel();
  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({
    id: `recording:${recording.id}`,
    data: { type: "recording", id: recording.id, label: recording.title },
  });

  const explanation = statusExplanation(recording);

  if (confirming) {
    return (
      <Card className="border-danger/40 p-5">
        <div className="flex items-start gap-3">
          <AlertTriangle size={17} className="mt-0.5 shrink-0 text-danger" />
          <div className="min-w-0 flex-1">
            <p className="text-[14px] font-medium text-text">
              {t("card.confirmDeleteTitle", { title: recording.title })}
            </p>
            <p className="mt-1 text-[13px] leading-relaxed text-text-muted">
              {t("card.confirmDeleteBody")}
            </p>
            <DeleteImpact recordingId={recording.id} />
            <div className="mt-3.5 flex gap-2">
              <Button
                variant="danger"
                size="sm"
                disabled={deleting}
                onClick={onConfirmDelete}
              >
                {deleting ? <Loader2 size={13} className="animate-spin" /> : null}
                {t("card.deletePermanently")}
              </Button>
              <Button variant="secondary" size="sm" onClick={onCancelDelete}>
                {t("common:actions.cancel")}
              </Button>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className={cn("group/row", isDragging && "opacity-40")}>
      {/*
        The drag listeners are on a dedicated grip, not on the link.
        
        Spreading them onto the <Link> produced `<a role="button"
        aria-roledescription="draggable">` with a <button> nested inside it:
        invalid HTML, no link semantics (so no open-in-new-tab), and an
        accessible name that read the entire card as one string.
      */}
      <Link
        to="/r/$recordingId"
        params={{ recordingId: recording.id }}
        className="block"
      >
        <Card
          className={cn(
            "group p-5 transition-all duration-150",
            "hover:border-border-strong hover:shadow-[var(--shadow-md)]",
          )}
        >
          {/* Wraps: on a phone, the live-state action set (Stop + badge +
              grip + delete) is wider than half the card, and a non-wrapping
              row starved the title down to a few characters — on exactly the
              card where reading it matters most. */}
          <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
            <div className="min-w-0 flex-1 basis-40">
              <h3 dir="auto" className="truncate text-[15px] font-semibold text-text">
                {recording.title}
              </h3>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[13px] text-text-muted">
                <span className="inline-flex items-center gap-1.5">
                  {recording.platform === "upload" ? (
                    <Upload size={13} strokeWidth={2} />
                  ) : importSourceFor(recording.platform) ? (
                    <Link2 size={13} strokeWidth={2} />
                  ) : (
                    <Radio size={13} strokeWidth={2} />
                  )}
                  {platformLabel(recording.platform)}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <CalendarClock size={13} strokeWidth={2} />
                  {f.dateTime(recording.startedAt, {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                </span>
                {recording.durationMs ? (
                  <span className="tabular-nums">
                    {f.duration(recording.durationMs)}
                  </span>
                ) : null}
                {recording.sizeBytes ? (
                  <span className="tabular-nums text-text-faint">
                    {f.bytes(recording.sizeBytes)}
                  </span>
                ) : null}
              </div>
            </div>
            {/*
              The badge and the delete control share one row, so the icon can
              never sit on top of the status. It fades in on hover but reserves
              its space always — a control that appears and shifts the layout is
              worse than one that is simply visible.
            */}
            <div className="flex shrink-0 items-center gap-1">
              {/*
                A grip, and the only element carrying the drag listeners. It is
                also focusable, so `onMoveTo` below gives keyboard and touch
                users a path that dragging never could — dnd-kit here registers
                only a PointerSensor.
              */}
              <button
                type="button"
                ref={setDragRef}
                {...listeners}
                {...attributes}
                aria-label={t("card.moveNamed", { title: recording.title })}
                title={t("card.dragHint")}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onMoveTo();
                }}
                className="touch-show cursor-grab rounded-md p-1.5 text-text-faint opacity-0 transition-colors hover:bg-surface-hover hover:text-text focus-visible:opacity-100 active:cursor-grabbing group-hover/row:opacity-100"
              >
                <GripVertical size={14} />
              </button>
              {/* Same reasoning as the detail page: a bot stuck at `scheduled`
                  is precisely when someone needs to stop it. */}
              {recording.status === "scheduled" || recording.status === "recording" ? (
                // Always visible, never hover-revealed: this is the only way to
                // finish a recording, and a control you have to discover is not
                // one you can rely on mid-meeting.
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={stopping}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onStop();
                  }}
                >
                  {stopping ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    <Square size={12} fill="currentColor" />
                  )}
                  {t("card.stop")}
                </Button>
              ) : null}
              <StatusBadge recording={recording} />
              <button
                type="button"
                aria-label={t("card.deleteNamed", { title: recording.title })}
                title={t("card.deleteRecording")}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onAskDelete();
                }}
                className={cn(
                  "rounded-md p-1.5 text-text-faint transition-colors",
                  "touch-show opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100",
                  "hover:bg-danger-subtle hover:text-danger",
                )}
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>

          {explanation ? (
            <p
              className={cn(
                "mt-3 text-[12.5px] leading-relaxed",
                // Something that will not resolve on its own reads differently
                // from a progress note, and must not look like one.
                recording.needsAttention ? "text-warning" : "text-text-muted",
              )}
            >
              {st(explanation)}
            </p>
          ) : null}
        </Card>
      </Link>
    </div>
  );
}


/**
 * Choose a folder, without a mouse.
 *
 * dnd-kit here registers only a PointerSensor, so dragging was the *only* way to
 * file a recording — impossible with a keyboard, and awkward on a touch screen.
 * The card's grip opens this instead of trying to make dragging accessible,
 * because a list of folders is better than a keyboard drag even when the drag
 * works.
 */
function MoveToFolderDialog({
  folders,
  onClose,
  onPick,
}: {
  folders: Array<{ id: string; name: string }>;
  onClose: () => void;
  onPick: (projectId: string | null) => void;
}) {
  const { t } = useTranslation(["library", "common"]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-5 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <Card
        className="w-full max-w-[380px] p-4"
        role="dialog"
        aria-modal="true"
        aria-label={t("folders.moveTo")}
      >
        <h2 className="mb-3 text-[15px] font-semibold text-text">{t("folders.moveTo")}</h2>

        <div className="max-h-[320px] space-y-0.5 overflow-y-auto">
          <button
            type="button"
            onClick={() => onPick(null)}
            className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[13px] text-text-muted transition-colors hover:bg-surface-hover hover:text-text"
          >
            <Inbox size={14} />
            {t("folders.home")}
          </button>

          {folders.map((folder) => (
            <button
              key={folder.id}
              type="button"
              onClick={() => onPick(folder.id)}
              className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[13px] text-text transition-colors hover:bg-surface-hover"
            >
              <Folder size={14} className="text-text-faint" />
              {folder.name}
            </button>
          ))}
        </div>

        <Button variant="secondary" className="mt-3 w-full" onClick={onClose}>
          {t("common:actions.cancel")}
        </Button>
      </Card>
    </div>
  );
}
